// src/modules/app-views/invoices/pages/Document/tabs/RecordTab/CarrierCard.jsx
// *******************************************************************************
// CARRIER CARD (TRANSPORTADORA)
// *******************************************************************************
// Card for selecting carrier entity and displaying transport/logistics details.
// Includes: carrier entity picker, freight modality, vehicle plate, volumes/weights.
// Only visible for NFe documents (not NFCe).
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Row, Col, Descriptions, Divider } from 'antd';
import SimpleSelectAsyncEntities from 'components/SimpleComponents/SimpleSelectAsyncEntities';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import { openModal } from 'components/core/GenericModal';
import EditEntityModal from 'modules/app-views/customers/components/EditEntityModal';
import translateX from 'utils/translateX';
import useZustand from 'hooks/useZustand';
import { FREIGHT_MODALITY } from 'services/fiscalService';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const CARRIER_PROFILES = ['carrier', 'company'];

const FREIGHT_MODALITY_OPTIONS = [
  { value: FREIGHT_MODALITY.NO_FREIGHT, labelKey: 'freight_no_freight' },
  { value: FREIGHT_MODALITY.SENDER, labelKey: 'freight_sender' },
  { value: FREIGHT_MODALITY.RECIPIENT, labelKey: 'freight_recipient' },
  { value: FREIGHT_MODALITY.THIRD_PARTY, labelKey: 'freight_third_party' },
  { value: FREIGHT_MODALITY.OWN_SENDER, labelKey: 'freight_own_sender' },
  { value: FREIGHT_MODALITY.OWN_RECIPIENT, labelKey: 'freight_own_recipient' },
];

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CarrierCard = ({ data, viewMode, onActionsChange }) => {
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  const { form } = useSimpleFormContext();
  const storedLanguage = useZustand('theme', 'language');

  // 3. LOCAL VARIABLES I
  const isLocked = viewMode === 'view';
  const isAuthorized = data?.status === 'authorized';

  const freightModalityOptions = useMemo(
    () =>
      FREIGHT_MODALITY_OPTIONS.map(opt => ({
        value: opt.value,
        label: translateX(opt.labelKey),
      })),
    [storedLanguage]
  );

  // 4. LOCAL STATE
  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const initRef = useRef(false);

  // 5. EFFECTS

  // Initialize carrier from hydrated entity or saved snapshot
  useEffect(() => {
    if (initRef.current || !data?._id) return;

    const carrierEntity = data?.carrierEntity;
    const carrierSnapshot = data?.logistics?.carrier;

    if (carrierEntity?._id) {
      initRef.current = true;
      setSelectedCarrier(carrierEntity);
      form?.setFieldsValue({ carrierId: carrierEntity._id });
    } else if (carrierSnapshot?.entityId) {
      initRef.current = true;
      setSelectedCarrier({
        _id: carrierSnapshot.entityId,
        name: carrierSnapshot.name || carrierSnapshot.tradeName,
        uniqueKey: carrierSnapshot.cpfCnpj,
      });
      form?.setFieldsValue({ carrierId: carrierSnapshot.entityId });
    }
  }, [data, form]);

  // Expose actions to parent for Collapse header buttons
  useEffect(() => {
    onActionsChange?.({
      handleNewCarrier,
      handleEditCarrier,
      selectedCarrier,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onActionsChange, selectedCarrier]);

  // 6. HANDLERS
  const handleEntitySelect = useCallback(
    entity => {
      setSelectedCarrier(entity);

      if (!entity) {
        form?.setFieldsValue({ carrierId: undefined, carrier: undefined });
        return;
      }

      const cnpjDoc = entity?.documents?.find?.(d => d.documentType === 'cnpj');
      const cpfDoc = entity?.documents?.find?.(d => d.documentType === 'cpf');
      const cpfCnpj = cnpjDoc?.value || cpfDoc?.value || entity?.uniqueKey || '';
      const ie = entity?.fiscalData?.stateRegistration || entity?.ie || '';
      const addr = entity?.address || entity?.addresses?.[0] || {};

      form?.setFieldsValue({
        carrierId: entity._id,
        carrier: {
          entityId: entity._id,
          cpfCnpj,
          name: entity.razaoSocial || entity.name,
          tradeName: entity.nomeFantasia || entity.tradeName || '',
          stateRegistration: ie,
          address: {
            address1: addr.address1 || addr.street || '',
            number: addr.number || '',
            neighborhood: addr.neighborhood || '',
            city: addr.city || '',
            region: addr.region || addr.state || '',
            zip: addr.zip || addr.zipCode || '',
            countryCode: addr.countryCode || 'BR',
          },
        },
      });
    },
    [form]
  );

  const handleNewCarrier = useCallback(() => {
    openModal(EditEntityModal, {
      entityProfile: 'company',
      afterSave: entity => {
        if (entity) handleEntitySelect(entity);
      },
    });
  }, [handleEntitySelect]);

  const handleEditCarrier = useCallback(() => {
    if (!selectedCarrier?._id) return;
    openModal(EditEntityModal, {
      entityId: selectedCarrier._id,
      entityProfile: 'company',
      afterSave: entity => {
        if (entity) handleEntitySelect(entity);
      },
    });
  }, [selectedCarrier, handleEntitySelect]);

  // 7. LOCAL VARIABLES II
  const carrierInfo = useMemo(() => {
    if (!selectedCarrier) return null;
    const snapshot = data?.logistics?.carrier || {};
    return {
      name: selectedCarrier.razaoSocial || selectedCarrier.name || snapshot.name || '',
      tradeName:
        selectedCarrier.nomeFantasia ||
        selectedCarrier.tradeName ||
        snapshot.tradeName ||
        '',
      cpfCnpj: selectedCarrier.uniqueKey || snapshot.cpfCnpj || '',
      ie:
        selectedCarrier.fiscalData?.stateRegistration ||
        selectedCarrier.ie ||
        snapshot.stateRegistration ||
        '',
    };
  }, [selectedCarrier, data?.logistics?.carrier]);

  // 8. UI
  return (
    <div>
      {/* Carrier entity picker + Freight Modality */}
      <Row gutter={[16, 0]}>
        <Col xs={24} md={12}>
          <SimpleSelectAsyncEntities
            name="carrierId"
            label="search_carrier"
            entityProfiles={CARRIER_PROFILES}
            disabled={isLocked || isAuthorized}
            onEntitySelect={handleEntitySelect}
            selectedEntity={selectedCarrier}
          />
        </Col>
        <Col xs={24} md={12}>
          <SimpleSelect
            name="freightModality"
            label="freight_modality"
            options={freightModalityOptions}
            disabled={isLocked || isAuthorized}
          />
        </Col>
      </Row>

      {/* Carrier info display */}
      {carrierInfo && (
        <>
          <Divider style={{ margin: '8px 0 12px' }} />
          <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
            <Descriptions.Item label={translateX('name')}>{carrierInfo.name}</Descriptions.Item>
            {carrierInfo.tradeName && (
              <Descriptions.Item label={translateX('trade_name')}>
                {carrierInfo.tradeName}
              </Descriptions.Item>
            )}
            {carrierInfo.cpfCnpj && (
              <Descriptions.Item label="CNPJ/CPF">{carrierInfo.cpfCnpj}</Descriptions.Item>
            )}
            {carrierInfo.ie && (
              <Descriptions.Item label="IE">{carrierInfo.ie}</Descriptions.Item>
            )}
          </Descriptions>
        </>
      )}

      {/* Vehicle and Volumes */}
      <Divider style={{ margin: '12px 0' }} />
      <Row gutter={[16, 0]}>
        <Col xs={24} md={8}>
          <SimpleInput
            name={['logistics', 'vehicle', 'plate']}
            label="vehicle_plate"
            disabled={isLocked || isAuthorized}
            placeholder="ABC1D23"
          />
        </Col>
        <Col xs={24} md={4}>
          <SimpleInput
            name={['logistics', 'vehicle', 'state']}
            label="vehicle_state"
            disabled={isLocked || isAuthorized}
            placeholder="SP"
            maxLength={2}
          />
        </Col>
        <Col xs={24} md={4}>
          <SimpleNumber
            name={['logistics', 'volumes', 'quantity']}
            label="volumes_quantity"
            disabled={isLocked || isAuthorized}
            min={0}
          />
        </Col>
        <Col xs={24} md={4}>
          <SimpleNumber
            name={['logistics', 'volumes', 'grossWeight']}
            label="gross_weight"
            disabled={isLocked || isAuthorized}
            min={0}
            decimalPlaces={3}
          />
        </Col>
        <Col xs={24} md={4}>
          <SimpleNumber
            name={['logistics', 'volumes', 'netWeight']}
            label="net_weight"
            disabled={isLocked || isAuthorized}
            min={0}
            decimalPlaces={3}
          />
        </Col>
      </Row>

      {/* Hidden fields for carrier snapshot */}
      <SimpleInput name="carrierId" hidden />
      <SimpleInput name={['carrier', 'entityId']} hidden />
      <SimpleInput name={['carrier', 'cpfCnpj']} hidden />
      <SimpleInput name={['carrier', 'name']} hidden />
      <SimpleInput name={['carrier', 'tradeName']} hidden />
      <SimpleInput name={['carrier', 'stateRegistration']} hidden />
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CarrierCard;
